///
/// EarthBrowser
///
/// Created by Matt Giger on 9/4/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useIsMobile } from "@/components/utils/use-mobile"
import { cLayersPanelEdgeInset, cLegendTrayWidth, cTimeControlHeightVar, cTimeControlWidthVar, useChromeInsets } from "@/hooks/useBottomChromeOffset"
import { useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { useEffect, useRef } from "react"
import { TimeStrip } from "./time/TimeStrip"
import { TransportRow } from "./time/TransportRow"
import { useTimeControlModel } from "./time/useTimeControlModel"
import { WindowRow } from "./time/WindowRow"

/**
 * TimelineBar - persistent time control pinned to the bottom-center of the
 * screen. This shell only positions the glass pill and publishes its size;
 * the rows render the view model from `useTimeControlModel`, which owns
 * every piece of time logic (see `time/timeControlModel.ts`).
 *
 * Collapsed: the strip (edge times, playhead, now marker, data lanes) over
 * one control row (play/speed, frame step, LIVE/clock chip, chevron).
 * Expanded (chevron): the history/forecast window row above the strip,
 * thicker lanes with a legend and a day ruler on multi-day windows.
 */
export function TimelineBar() {
	const model = useTimeControlModel()
	const isExpanded = model.isExpanded
	// Phones: the focused-entity info sheet docks over this bar; slide out
	// of the way while it's up rather than stacking two bottom surfaces
	const infoSheetOpen = useAppSelector(state => state.dialog.mobileInfoSheetOpen)

	// Keep the bar above the bottom drawer when it has docked tabs, and
	// centered in the globe area left of a right-side data dock (shared
	// with the camera stack so the two bottom surfaces stay aligned)
	const insets = useChromeInsets()
	const isMobile = useIsMobile()

	// Publish the rendered size so other bottom chrome can keep clear: the
	// height lets the camera stack and legend chips ride above the bar on
	// phones (where it spans the full width); the width feeds this bar's own
	// left-edge floor beside the legend tray on desktop
	const rootRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const el = rootRef.current
		if (!el) return
		const publish = () => {
			const rect = el.getBoundingClientRect()
			document.documentElement.style.setProperty(cTimeControlHeightVar, `${Math.round(infoSheetOpen ? 0 : rect.height)}px`)
			document.documentElement.style.setProperty(cTimeControlWidthVar, `${Math.round(rect.width)}px`)
		}
		publish()
		const observer = new ResizeObserver(publish)
		observer.observe(el)
		return () => {
			observer.disconnect()
			document.documentElement.style.removeProperty(cTimeControlHeightVar)
			document.documentElement.style.removeProperty(cTimeControlWidthVar)
		}
	}, [infoSheetOpen])

	return (
		<div
			className={cn(
				// Above the camera HUD (CameraControls, z-30) so the expanded
				// panel wins where the two overlap on narrow screens; still
				// below anchored info panels (z-40) and dialogs
				"fixed left-1/2 -translate-x-1/2 z-[35]",
				// no overflow-hidden: the scrub bubble floats above the pill
				"flex flex-col select-none",
				"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl",
				"transition-all duration-300",
				isExpanded ? "opacity-95" : "opacity-85 hover:opacity-95",
				// Phones: span the viewport (the control row may wrap while the
				// date editor is open). Desktop: a fixed width every normal
				// state fits in, so the ticking offset / legend / window row
				// never resize the pill; only the open date editor may grow it
				"w-[calc(100vw-16px)] sm:w-auto sm:min-w-[440px] max-w-[calc(100vw-16px)]",
				infoSheetOpen && "translate-y-[calc(100%+32px)] opacity-0 pointer-events-none"
			)}
			style={{
				bottom: insets.bottom,
				// Center in the globe area between the Layers panel and the data
				// dock, but never so far left that the bar's left edge lands on
				// the legend tray (bottom-left at 16px, cLegendTrayWidth wide,
				// on this baseline) or on the open Layers panel (insets.left).
				// `left` is the bar's center (translate-x-1/2), so the floor
				// adds half the published width. Phones: full-width bar, the
				// tray rides above it instead
				left: isMobile
					? undefined
					: `max(calc(50% + ${(insets.left - insets.right) / 2}px), calc(${Math.max(insets.left, cLayersPanelEdgeInset + cLegendTrayWidth) + 8}px + var(${cTimeControlWidthVar}, 440px) / 2))`,
			}}
			aria-hidden={infoSheetOpen || undefined}
			ref={rootRef}
			data-time-control
		>
			{/* Window row, directly above the strip so the history stepper
			    sits over the track's history end and the forecast stepper
			    over the forecast end */}
			<div
				className={cn(
					"overflow-hidden transition-all duration-300 ease-in-out",
					isExpanded ? "max-h-16 opacity-100" : "max-h-0 opacity-0"
				)}
				aria-hidden={!isExpanded || undefined}
			>
				<WindowRow model={model} />
			</div>

			<TimeStrip model={model} />
			<TransportRow model={model} />

			<style>{`
				@keyframes timelinePulse {
					0%, 100% { opacity: 1; transform: scale(1); }
					50% { opacity: 0.5; transform: scale(0.8); }
				}
				@keyframes timelineShimmer {
					from { transform: translateX(-100%); }
					to { transform: translateX(300%); }
				}
				.eb-lane-shimmer {
					background: linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent);
					animation: timelineShimmer 1.6s ease-in-out infinite;
				}
				.eb-lane-pulse { animation: timelinePulse 1.4s ease-in-out infinite; }
				@media (prefers-reduced-motion: reduce) {
					.eb-lane-shimmer { animation: none; background: rgba(255,255,255,0.5); width: 100%; }
					.eb-lane-pulse { animation: none; }
				}
			`}</style>
		</div>
	)
}
